import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name?.trim()?.split(' ');
  if (parts?.length >= 2) return (parts?.[0]?.charAt(0) + parts?.[1]?.charAt(0))?.toUpperCase();
  return name?.trim()?.charAt(0)?.toUpperCase();
};

const ServerBadge = ({ name, color }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '4px',
      fontSize: '11px',
      fontWeight: '700',
      backgroundColor: `${color}18`,
      color,
      border: `1px solid ${color}30`,
      fontFamily: 'Sora, sans-serif',
    }}
  >
    {name}
  </span>
);

const ManagerList = () => {
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [managers, setManagers] = useState([]);
  const [servers, setServers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('diamonds');
  const [sortDir, setSortDir] = useState('desc');
  const navigate = useNavigate();

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersResult, serversResult, creatorMonthResult] = await Promise.all([
        supabase?.from('users')?.select('id, name, email, role, managed_servers, servers')?.in('role', ['manager', 'region_manager'])?.order('name'),
        supabase?.from('servers')?.select('id, name, color')?.order('name'),
        supabase?.from('creator_current_month')?.select('manager_id, is_active, diamonds, is_new_recruit'),
      ]);

      setServers(serversResult?.data || []);

      const creatorRows = creatorMonthResult?.data || [];

      // Aggregate by manager_id
      const statsMap = {};
      creatorRows?.forEach((row) => {
        const mid = row?.manager_id;
        if (!mid) return;
        if (!statsMap?.[mid]) {
          statsMap[mid] = { total: 0, actifs: 0, diamonds: 0, nouveaux: 0 };
        }
        statsMap[mid].total += 1;
        if (row?.is_active) statsMap[mid].actifs += 1;
        statsMap[mid].diamonds += row?.diamonds || 0;
        if (row?.is_new_recruit === true) statsMap[mid].nouveaux += 1;
      });

      setStats(statsMap);
      setManagers(usersResult?.data || []);
    } catch (err) {
      console.error('ManagerList fetchData error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const serverColorMap = {};
  servers?.forEach((s) => { serverColorMap[s.id] = s?.color || '#6b7280'; });

  const formatDiamonds = (n) => {
    if (!n) return '0';
    if (n >= 1000000) return (n / 1000000)?.toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000)?.toFixed(1) + 'K';
    return String(n);
  };

  const sortedManagers = [...managers]?.sort((a, b) => {
    const mult = sortDir === 'desc' ? -1 : 1;
    const aStats = stats?.[a?.id] || { total: 0, actifs: 0, diamonds: 0, nouveaux: 0 };
    const bStats = stats?.[b?.id] || { total: 0, actifs: 0, diamonds: 0, nouveaux: 0 };
    const fieldMap = {
      diamonds: [aStats?.diamonds, bStats?.diamonds],
      actifs: [aStats?.actifs, bStats?.actifs],
      total: [aStats?.total, bStats?.total],
      nouveaux: [aStats?.nouveaux, bStats?.nouveaux],
    };
    const [aVal, bVal] = fieldMap?.[sortField] || [0, 0];
    return mult * (aVal - bVal);
  });

  const SortArrow = ({ field }) => {
    if (sortField !== field) return <span style={{ color: 'rgba(255,255,255,0.2)', marginLeft: '4px' }}>↕</span>;
    return <span style={{ color: '#22c55e', marginLeft: '4px' }}>{sortDir === 'desc' ? '↓' : '↑'}</span>;
  };

  const sortableHeaders = [
    { key: null, label: t('admin.managerList.colManager', 'Manager') },
    { key: null, label: t('admin.managerList.colServers', 'Serveurs') },
    { key: 'total', label: t('admin.managerList.colCreators', 'Nb Créateurs') },
    { key: 'actifs', label: t('admin.managerList.colActiveCreators', 'Créateurs Actifs') },
    { key: 'diamonds', label: t('admin.managerList.colDiamonds', 'Total Diamonds (mois)') },
    { key: 'nouveaux', label: t('admin.managerList.colNewRecruits', 'Nouveaux') },
  ];

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>
      <div className="hidden md:flex">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed((c) => !c)} userEmail="" />
      </div>
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
        <AdminMobileHeader title={t('admin.managerList.title', 'Managers')} />
        <div className="px-6 py-6 max-w-full">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{t('admin.managerList.title', 'Managers')}</h1>
            <p className="text-sm mt-0.5" style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>
              {t('admin.managerList.subtitle', 'Vue d\'ensemble des managers et leurs créateurs')}
            </p>
          </div>

          {/* Table */}
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #1f1f1f', backgroundColor: '#0d0d0d' }}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                    {sortableHeaders?.map((col) => (
                      <th
                        key={col?.label}
                        className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                        style={{
                          color: col?.key && sortField === col?.key ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.3)',
                          fontFamily: 'Sora, sans-serif',
                          backgroundColor: '#0d0d0d',
                          cursor: col?.key ? 'pointer' : 'default',
                          userSelect: 'none',
                          whiteSpace: 'nowrap',
                        }}
                        onClick={() => col?.key && handleSort(col?.key)}
                      >
                        {col?.label}
                        {col?.key && <SortArrow field={col?.key} />}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(5)]?.map((_, i) => (
                      <tr key={i}>
                        {[...Array(6)]?.map((__, j) => (
                          <td key={j} className="px-4 py-3">
                            <div className="h-4 rounded animate-pulse" style={{ backgroundColor: '#1f1f1f', width: j === 0 ? '140px' : '80px' }} />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : sortedManagers?.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-sm" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
                        {t('admin.managerList.noManagerFound', 'Aucun manager trouvé')}
                      </td>
                    </tr>
                  ) : (
                    sortedManagers?.map((mgr) => {
                      const mgrStats = stats?.[mgr?.id] || { total: 0, actifs: 0, diamonds: 0, nouveaux: 0 };
                      const displayServers = Array.isArray(mgr?.managed_servers) && mgr?.managed_servers?.length > 0
                        ? mgr?.managed_servers
                        : (Array.isArray(mgr?.servers) ? mgr?.servers : []);
                      const avatarColor = mgr?.role === 'region_manager' ? '#a855f7' : '#3b82f6';

                      return (
                        <tr
                          key={mgr?.id}
                          style={{ borderBottom: '1px solid #141414', cursor: 'pointer' }}
                          onClick={() => navigate(`/admin/managers/${mgr?.id}`)}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        >
                          {/* Manager name + avatar */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div
                                className="flex-shrink-0 rounded-full flex items-center justify-center text-white font-bold text-xs"
                                style={{ width: '34px', height: '34px', backgroundColor: avatarColor, fontFamily: 'Sora, sans-serif' }}
                              >
                                {getInitials(mgr?.name)}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-white" style={{ fontFamily: 'Inter, sans-serif' }}>{mgr?.name || '—'}</p>
                                <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}>{mgr?.email}</p>
                              </div>
                            </div>
                          </td>
                          {/* Servers badges */}
                          <td className="px-4 py-3">
                            {displayServers?.length === 0 ? (
                              <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '13px' }}>—</span>
                            ) : (
                              <div className="flex gap-1 flex-wrap">
                                {displayServers?.map((serverId) => {
                                  const server = servers?.find((s) => s?.id === serverId);
                                  return (
                                    <ServerBadge
                                      key={serverId}
                                      name={serverId}
                                      color={server?.color || serverColorMap?.[serverId] || '#6b7280'}
                                    />
                                  );
                                })}
                              </div>
                            )}
                          </td>
                          {/* Nb créateurs total */}
                          <td className="px-4 py-3">
                            <span className="text-sm font-semibold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
                              {mgrStats?.total}
                            </span>
                          </td>
                          {/* Nb créateurs actifs */}
                          <td className="px-4 py-3">
                            <span
                              className="text-sm font-semibold"
                              style={{ color: mgrStats?.actifs > 0 ? '#22c55e' : 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}
                            >
                              {mgrStats?.actifs}
                            </span>
                          </td>
                          {/* Total diamonds */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <span style={{ fontSize: '14px' }}>💎</span>
                              <span
                                className="text-sm font-semibold"
                                style={{ color: mgrStats?.diamonds > 0 ? '#a78bfa' : 'rgba(255,255,255,0.35)', fontFamily: 'Inter, sans-serif' }}
                              >
                                {formatDiamonds(mgrStats?.diamonds)}
                              </span>
                            </div>
                          </td>
                          {/* Nouveaux recrutés */}
                          <td className="px-4 py-3">
                            <span
                              className="text-sm font-semibold"
                              style={{ color: mgrStats?.nouveaux > 0 ? '#22c55e' : 'rgba(255,255,255,0.25)', fontFamily: 'Inter, sans-serif' }}
                            >
                              {mgrStats?.nouveaux > 0 ? mgrStats?.nouveaux : '—'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {!loading && (
              <div className="px-4 py-3 border-t" style={{ borderColor: '#1f1f1f' }}>
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif' }}>
                  {t('admin.managerList.managerCount', '{{count}} manager(s)', { count: managers?.length })}
                </span>
              </div>
            )}
          </div>
        </div>
      </main>
      <AdminBottomNav />
    </div>
  );
};

export default ManagerList;
